import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { ResourceService } from '../resource/resource.service';
import { DatabaseService } from '../database.service';

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private databaseService: DatabaseService,
        private resourceService: ResourceService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: any): Observable<any> {
      const params = { email: email };
        return this.resourceService.post("users/forgot-password", params).pipe(
          tap((response: any) => {
            console.log('forgot-password',response)
          }),
          catchError((error: any) => {
            return throwError(error);
          })
        );
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(data: any){
      return new Promise((resolve, reject) => {
        this.resourceService.put("users/:id/password", data).subscribe(
          (data: any) => {
            resolve(true);
          },
          (err) => {
            reject(false);
          }
        );
      });
    }

    /**
     * Forgot password
     *
     * @param password
     */
    resetForgotPassword(pass: any, registerId: any){
      const data = {
        password: pass,
        registerId: registerId
      }
      return this.resourceService.put("users/reset-password", data).pipe(
        tap((response: any) => {}),
        catchError((error: any) => {
          return throwError(error);
        })
      );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this.resourceService.post('auth/login', credentials)
            .pipe(
                switchMap((response: any) => {

                    this.databaseService.set('token', response.accessToken);

                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return a new observable with the response
                    return of(response);

                }),
                catchError((error: any) => {
                    return throwError(error);
                })
            );

    }



    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * doLogin from taxiar
     */
    doLogin(value) {
        return new Promise<any>((resolve, reject) => {
          this.resourceService.post("/users/register", value).subscribe(
            (data: any) => {
              this.databaseService.set('token', data.accessToken);
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
        });
      }



    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('id');
        localStorage.removeItem('email');
        localStorage.removeItem('firstName');
        localStorage.removeItem('lastName');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: any) {
      user.state = 'inhabilitado';

      return this.resourceService.post("users/register", user).pipe(
        tap((response: any) => {
          this.databaseService.set('token', response.accessToken);
        }),
        catchError((error: any) => {
          return throwError(error);
        })
      );
    }

    /**
     * Activate User
     */
    activate(registerId: any) {
        return this.resourceService.put("users/:registerId/confirm", registerId).pipe(
            tap((response: any) => {
            }),
            catchError((error: any) => {
              return throwError(error);
            })
          );
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }


    /**
     * Agregado from Taxiar las funciones que siguen
     */
    doRegister(value) {
        return new Promise<any>((resolve, reject) => {
        });
    }


    getUsers(value){
        return new Promise<any>((resolve, reject) => {
          this.resourceService.get("/users", value).subscribe(
            (data: any) => {
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
        });
      }


      doLogout() {
        return new Promise((resolve, reject) => {
          this.resourceService.get("/auth/logout","").subscribe(
            (data: any) => {
              this.databaseService.delete('token');
              resolve(true);
            },
            (err) => {
              reject(false);
            }
          );
        });
      }

      changePassword(data: any){
        return new Promise((resolve, reject) => {
          this.resourceService.put("/users/password",data).subscribe(
            (data: any) => {
              resolve(true);
            },
            (err) => {
              reject(false);
            }
          );
        });
      }

      resetPassword_ar(data: any){
        let id = data.id;
        return new Promise((resolve, reject) => {
          this.resourceService.put("/users/:id/password",data).subscribe(
            (data: any) => {
              resolve(true);
            },
            (err) => {
              reject(false);
            }
          );
        });
      }

    /**
     * Get menu details
     */
    getMenuDetails(): Observable<any> {
        return this.resourceService.get("users/menu", {}).pipe(
          tap((response: any) => {
              return response;
          }),
          catchError((error: any) => {
            return throwError(error);
          })
        );
      }
}
